import { useEffect, useState } from "react";
import { KeyRound, Save, ShieldCheck, User } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { fmtDateTime } from "../utils/format";

const ROLE_LABELS = { admin: "Administrateur", comptable: "Comptable", commercial: "Commercial", gestionnaire: "Gestionnaire" };

export default function Profile() {
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });

  useEffect(() => {
    setProfile({ name: user?.name || "", phone: user?.phone || "" });
  }, [user]);

  const saveProfile = async () => {
    try {
      const r = await api.patch("/auth/profile", profile);
      setUser(r.data);
      toast.success("Profil mis à jour");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const savePassword = async () => {
    if (pw.new_password.length < 8) return toast.error("8 caractères minimum");
    if (pw.new_password !== pw.confirm) return toast.error("Les mots de passe ne correspondent pas");
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Mot de passe modifié");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="profile-page">
      <div className="flex items-center gap-4">
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-16 w-16 rounded-full border-2 border-cyan-500/40 object-cover" data-testid="profile-avatar" />
        ) : (
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0F172A] text-2xl font-bold text-cyan-400">
            {(user?.name || "U")[0].toUpperCase()}
          </span>
        )}
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{user?.name}</h1>
          <p className="text-sm text-slate-500">{user?.email}</p>
          <div className="mt-1 flex gap-2">
            <Badge variant="outline" className="capitalize">{ROLE_LABELS[user?.role] || user?.role}</Badge>
            {user?.auth_provider === "google" && <Badge variant="outline" className="border-cyan-200 bg-cyan-50 text-cyan-700">Compte Google</Badge>}
          </div>
        </div>
      </div>

      <Tabs defaultValue={params.get("tab") === "securite" ? "securite" : "profil"}>
        <TabsList>
          <TabsTrigger value="profil" data-testid="profile-tab-profil"><User className="mr-1 h-4 w-4" /> Mon profil</TabsTrigger>
          <TabsTrigger value="securite" data-testid="profile-tab-securite"><ShieldCheck className="mr-1 h-4 w-4" /> Sécurité</TabsTrigger>
        </TabsList>

        <TabsContent value="profil" className="mt-4">
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div><Label>Nom complet</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" /></div>
            <div><Label>Téléphone</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone-input" /></div>
            <div><Label>Email</Label><Input value={user?.email} disabled className="bg-slate-50" /></div>
            <p className="text-xs text-slate-400">Compte créé le {fmtDateTime(user?.created_at)}</p>
            <Button onClick={saveProfile} data-testid="profile-save-button"><Save className="mr-1 h-4 w-4" /> Enregistrer</Button>
          </div>
        </TabsContent>

        <TabsContent value="securite" className="mt-4">
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="font-heading flex items-center gap-2 text-base font-semibold text-slate-800">
              <KeyRound className="h-4 w-4 text-cyan-600" /> Changer le mot de passe
            </h3>
            {user?.auth_provider === "google" && !user?.has_password ? (
              <p className="text-sm text-slate-500">Votre compte est lié à Google. Aucun mot de passe local n'est défini.</p>
            ) : (
              <>
                <div><Label>Mot de passe actuel</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" /></div>
                <div><Label>Nouveau mot de passe</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" /></div>
                <div><Label>Confirmation</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" /></div>
                <Button onClick={savePassword} data-testid="pw-save-button">Modifier le mot de passe</Button>
              </>
            )}
            <div className="rounded-lg bg-slate-50 p-4 text-xs text-slate-500">
              <p className="font-semibold text-slate-600">Sécurité du compte</p>
              <p className="mt-1">Mots de passe hashés (bcrypt) · sessions à expiration automatique · verrouillage après 5 tentatives échouées · routes protégées côté serveur.</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
